import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { useEffect, useRef } from 'react'

import { KestrelCommands } from './types/KestrelCommands'

const SCREENSHOT_TIMEOUT_MS = 10000
const POLLING_INTERVAL_MS = 100
const MAX_POLLING_ATTEMPTS = SCREENSHOT_TIMEOUT_MS / POLLING_INTERVAL_MS

export interface ScreenshotParams {
  position: string
  rotation: string
  width: number
  height: number
  fov: number
  annotationIds?: string[]
  surfaceAreaIds?: string[]
  showAnnotationMarkers?: boolean
  entryId?: string
  rolodexPath?: string
  uploadToRolodex?: boolean
}

export interface ScreenshotResult {
  success: boolean
  imageBase64?: string
  width?: number
  height?: number
  position?: string
  rotation?: string
  error?: string
}
interface KestrelScreenshotBridge {
  captureScreenshot: (params: ScreenshotParams) => Promise<ScreenshotResult>
}
declare global {
  interface Window {
    kestrelScreenshotBridge?: KestrelScreenshotBridge
  }
}

/**
 * We have to poll here to await response from VM
 */
const pollForScreenshotResult = (
  requestId: string,
  contextRef: React.RefObject<ReturnType<typeof useKestrelViewerContext>>
): Promise<ScreenshotResult> => {
  return new Promise((resolve) => {
    let attempts = 0
    let isResolved = false

    const checkResult = () => {
      if (isResolved) return

      attempts++

      const currentContext = contextRef.current
      if (!currentContext) {
        isResolved = true
        resolve({
          success: false,
          error: 'Context is no longer available',
        })
        return
      }

      const currentResult = currentContext.screenshotResult

      if (currentResult && currentResult.requestId === requestId) {
        isResolved = true

        if (currentResult.success) {
          resolve({
            success: true,
            imageBase64: currentResult.imageBase64,
            width: currentResult.width,
            height: currentResult.height,
            position: currentResult.position,
            rotation: currentResult.rotation,
          })
        } else {
          resolve({
            success: false,
            error: 'Screenshot capture failed in viewer',
          })
        }
      } else if (attempts >= MAX_POLLING_ATTEMPTS) {
        isResolved = true
        resolve({
          success: false,
          error: 'Screenshot capture timeout - no response from viewer',
        })
      } else {
        setTimeout(checkResult, POLLING_INTERVAL_MS)
      }
    }

    checkResult()
  })
}

/**
 * Exposes Kestrel screenshot functionality globally for components outside KestrelViewerProvider context
 */
export const KestrelScreenshotBridge: React.FC = () => {
  const kestrelContext = useKestrelViewerContext()
  const contextRef = useRef(kestrelContext)
  contextRef.current = kestrelContext

  useEffect(() => {
    const bridgeFunction = async (
      params: ScreenshotParams
    ): Promise<ScreenshotResult> => {
      if (!params.position || !params.rotation) {
        return {
          success: false,
          error: 'Position and rotation parameters are required',
        }
      }

      if (params.width <= 0 || params.height <= 0) {
        return {
          success: false,
          error: 'Width and height must be positive numbers',
        }
      }

      const currentContext = contextRef.current

      if (!currentContext.sendCommand) {
        return { success: false, error: 'sendCommand not available' }
      }

      if (!currentContext.setScreenshotResult) {
        return { success: false, error: 'setScreenshotResult not available' }
      }

      try {
        currentContext.setScreenshotResult(null)

        const requestId = `bridge_${Date.now()}`

        const commandToSend = {
          command: KestrelCommands.CAPTURE_POSE_SCREENSHOT,
          requestId,
          position: params.position,
          rotation: params.rotation,
          width: params.width,
          height: params.height,
          fov: params.fov,
          surfaceAreaIds: params.surfaceAreaIds,
          annotationIds: params.annotationIds,
          showAnnotationMarkers: params.showAnnotationMarkers,
          entryId: params.entryId,
          rolodexPath: params.rolodexPath,
          uploadToRolodex: params.uploadToRolodex,
        }

        currentContext.sendCommand(commandToSend)

        return await pollForScreenshotResult(requestId, contextRef)
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      }
    }

    const typedBridge: KestrelScreenshotBridge = {
      captureScreenshot: bridgeFunction,
    }
    window.kestrelScreenshotBridge = typedBridge

    return () => {
      delete window.kestrelScreenshotBridge
    }
  }, [])

  return null
}
